import { H5, Stack, P, Box, Inline, Button, H4 } from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import Divider from "components/Divider";
import { useFormContext } from "react-hook-form";
import { formKey } from "./util/formKey";
import { useNestedFormData } from "../useNestedFormData";
import { getDefaultNotification } from "./custom/defaultNotification";
import {
  getDefaultMetaTemplate,
  getDefaultDeltaTemplate,
  getDefaultCustomTemplate
} from "./defaultTemplateValues";
import { useParams } from "react-router-dom";
import { useFeatureToggle } from "hooks/useFeatureToggle";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";
import { useTranslation } from "react-i18next";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";

interface Props {
  bundleGroupIndex: number;
  bundleIndex: number;
  onSelect: () => void;
}

/**
 * When you select one of the options it should create a new campaign template
 * and increment the current campaign template index.
 */
export const BundleContentSelector = ({
  bundleGroupIndex,
  bundleIndex,
  onSelect
}: Props) => {
  const { t } = useTranslation();

  const { hqId } = useParams<HqIdRouteParams>();
  const { setValue } = useFormContext<ProgramService.ProgramModel>();
  const [_, bundleKey] = formKey(bundleGroupIndex, bundleIndex, 0, "meta");
  const {
    bundle: { metaTemplates, deltaTemplates, customTemplates, tickets }
  } = useNestedFormData({
    bundleGroupIndex,
    bundleIndex
  });
  const { hq } = useOfficeAndHq();

  const firstTemplate =
    (metaTemplates?.length ?? 0) === 0 && (deltaTemplates?.length ?? 0) === 0;

  const sellerPaidEnabled = useFeatureToggle(hqId ?? "", "seller-paid", false);

  return (
    <Stack spacing="m" p="gutter">
      <Box style={{ marginBottom: "24px" }}>
        <H4>{t("programBuilder.program.template.content.header.label")}</H4>
        <P>{t("programBuilder.program.template.content.header.description")}</P>
      </Box>

      <Inline justifyContent="space-between" alignItems="center" noWrap>
        <Box>
          <H5>{t("programBuilder.program.template.content.meta.header")}</H5>
          <P>{t("programBuilder.program.template.content.meta.description")}</P>
        </Box>
        <Button
          logLabel="add-meta-template-button"
          data-testid="add-meta-template"
          variant="secondary"
          onClick={() => {
            setValue(`${bundleKey}.metaTemplates`, [
              ...(metaTemplates ?? []),
              getDefaultMetaTemplate(firstTemplate ? 100 : 0)
            ]);
            onSelect();
          }}
        >
          {t("programBuilder.program.template.content.button.select")}
        </Button>
      </Inline>

      <Divider margin="16px 0" />

      <Inline justifyContent="space-between" alignItems="center" noWrap>
        <Box>
          <H5>{t("programBuilder.program.template.content.delta.header")}</H5>
          <P>
            {t("programBuilder.program.template.content.delta.description")}
          </P>
        </Box>
        <Button
          logLabel="add-delta-template-button"
          data-testid="add-delta-template"
          variant="secondary"
          onClick={() => {
            setValue(`${bundleKey}.deltaTemplates`, [
              ...(deltaTemplates ?? []),
              getDefaultDeltaTemplate(firstTemplate ? 100 : 0)
            ]);

            onSelect();
          }}
        >
          {t("programBuilder.program.template.content.button.select")}
        </Button>
      </Inline>

      <Divider margin="16px 0" />

      <Inline justifyContent="space-between" noWrap alignItems="center">
        <Box>
          <H5>{t("programBuilder.program.template.content.custom.header")}</H5>
          <P>
            {t("programBuilder.program.template.content.custom.description")}
          </P>
        </Box>
        <Button
          logLabel="add-custom-template-button"
          data-testid="add-custom-template"
          variant="secondary"
          disabled={!sellerPaidEnabled}
          onClick={() => {
            setValue(`${bundleKey}.customTemplates`, [
              ...(customTemplates ?? []),
              getDefaultCustomTemplate(hq)
            ]);

            onSelect();
          }}
        >
          {t("programBuilder.program.template.content.button.select")}
        </Button>
      </Inline>

      <Divider margin="16px 0" />

      <Inline justifyContent="space-between" alignItems="center" noWrap>
        <Box>
          <H5>
            {t("programBuilder.program.template.content.notification.header")}
          </H5>
          <P>
            {t(
              "programBuilder.program.template.content.notification.description"
            )}
          </P>
        </Box>
        <Button
          logLabel="bundle-add-notification-button"
          data-testid="add-notification"
          variant="secondary"
          disabled={!sellerPaidEnabled}
          onClick={() => {
            const ticketsOrDefault = tickets ?? [];
            setValue(`${bundleKey}.tickets`, [
              ...ticketsOrDefault,
              getDefaultNotification()
            ]);
            onSelect();
          }}
        >
          {t("programBuilder.program.template.content.button.select")}
        </Button>
      </Inline>

      <Divider margin="16px 0" />
    </Stack>
  );
};
